import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import OrderlikeLayout from '../../../../../generic/Layouts/OrderlikeLayout'
import AppModal from '../../../../../generic/ui/Modal/AppModal'
import ConfirmDialog from '../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../generic/grid-table'
import { GridColumn } from '../../../../../generic/grid-table/types/grid.types'
import PackageForm from './components/PackageForm'
import useTableData from '../../../../../generic/universal-table/hooks/useTableData'
import useTableEditing from '../../../../../generic/universal-table/hooks/useTableEditing'
import useTableFilters from '../../../../../generic/universal-table/hooks/useTableFilters'
import { HARDCODED_PACK_TYPES } from '../../../../../generic/artcoded/packtType'
import { packageTableService } from './services/packageTable.service'
import { packageTableColumns } from './configs/packageTable.columns'
import { packageTableFormFields } from './configs/packageTable.form'
import { packageTableAdapter } from './adapters/packageTable.adapter'
import {
  PackageCreatePayload,
  PackageUiRow,
  PackageUpdatePayload,
} from './types/packageTable.types'
import { ColumnDefinition } from '../../../../../generic/universal-table/types/column.types'
import { FormFieldDefinition } from '../../../../../generic/universal-table/types/form.types'
import { IClientItem } from '../../../../../modules/API/Defaults/models/clients'
import { Defaults } from '../../../../../modules/API/Defaults/defaults'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'
import useExportToExcel from '../../../../../generic/hooks/useExportToExcel'
import columnsToExcel from '../../../../../generic/universal-table/utils/columnsToExcel'

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  color: tomato;
  direction: rtl;
  padding: 0.5em 1em;
`

const PackageTableModule = () => {
  const {
    rows,
    isLoading,
    error,
    createRow,
    updateRow,
    removeRow,
  } = useTableData<PackageUiRow, PackageCreatePayload, PackageUpdatePayload>(
    packageTableService,
    packageTableAdapter.getRowId
  )

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<PackageUiRow>()

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<PackageUiRow>(rows)

  const [growers, setGrowers] = useState<IClientItem[]>([])
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [growersRes]: [IClientItem[]] = await Promise.all([
          Defaults.getClientsGo(),
        ])
        setGrowers(growersRes.filter(g => g.ClientType === '3'))
      } catch (error) {
        handleNotifications('שגיאה בטעינת הנתונים', {type: 'error'})
      }
      
    }
    fetchData()
  }, [])

  const growerOptions = useMemo(
    () => [{ value: 0, label: 'ללא ספק' }, ...growers.map((g) => ({ value: g.ClientID, label: g.ClientName }))],
    [growers]
  )

  const packTypeOptions = useMemo(
    () => HARDCODED_PACK_TYPES.map((item) => ({ value: item.id, label: item.name })),
    []
  )

  const enrichedColumns = useMemo<ColumnDefinition<PackageUiRow>[]>(
    () =>
      packageTableColumns.map((col) => {
        if (col.key === 'SupplierID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              growerOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        if (col.key === 'Pack_Type') {
          return {
            ...col,
            labelResolver: (type: unknown) =>
              packTypeOptions.find((o) => o.value === type)?.label ?? String(type),
          }
        }
        return col
      }),
    [growerOptions, packTypeOptions]
  )

  const enrichedFormFields = useMemo<FormFieldDefinition<PackageUiRow>[]>(
    () =>
      packageTableFormFields.map((field) => {
        if (field.name === 'SupplierID') {
          return { ...field, getOptions: () => growerOptions }
        }
        if (field.name === 'Pack_Type') {
          return { ...field, getOptions: () => packTypeOptions }
        }
        return field
      }),
    [growerOptions, packTypeOptions]
  )

  // Grid view reuses the enriched columns and layers on richer renderers.
  const gridColumns = useMemo<GridColumn<PackageUiRow>[]>(
    () =>
      enrichedColumns.map((col): GridColumn<PackageUiRow> => {
        if (col.key === 'PackID') return { ...col, cellRendererType: 'num' }
        if (col.key === 'Pack_Price') {
          return { ...col, cellRendererType: 'money', currencySuffix: '₪' }
        }
        return col
      }),
    [enrichedColumns]
  )

  const excelColumns = useMemo(() => columnsToExcel(enrichedColumns), [enrichedColumns])
  const { handleExport } = useExportToExcel({ columns: excelColumns, fileName: 'packages', rtl: true })

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleFormSubmit = useCallback(
    async (values: Partial<PackageUiRow>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          const id = packageTableAdapter.getRowId(selectedRow)
          const payload = packageTableAdapter.toUpdatePayload({
            ...selectedRow,
            ...values,
          } as PackageUiRow)
          await updateRow(id, payload)
        } else {
          const payload = packageTableAdapter.toCreatePayload(values as PackageUiRow)
          await createRow(payload)
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createRow, updateRow, handleCloseForm]
  )

  const formTitle = editingMode === 'edit' ? 'עריכת אריזה' : 'הוספת אריזה חדשה'

  return (
    <TableSection>
      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={PackageForm}
          childrenProps={{
            fields: enrichedFormFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size="md"
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title="מחיקת אריזה"
        message="האם למחוק את האריזה? פעולה זו אינה הפיכה."
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() =>
          handleConfirmDelete((row) => removeRow(packageTableAdapter.getRowId(row)))
        }
      />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <GridTable<PackageUiRow>
        title="אריזות"
        count={filteredRows.length}
        columns={gridColumns}
        rows={filteredRows}
        getRowId={packageTableAdapter.getRowId}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder="חיפוש אריזה…"
            onExport={() => handleExport(filteredRows)}
            onAdd={handleOpenCreate}
            addLabel="הוסף אריזה"
          />
        }
      />
    </TableSection>
  )
}

export default PackageTableModule
